import { useMutation } from "@tanstack/react-query";

import Sentry from "@similarweb/sentry-logger";

import type { ISetupPromptsResponse } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/types";
import { groupGeneratedPromptsByTopic } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/utils";
import type { ExplicitLLModel } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { DefaultFetchService } from "app/services/fetchService";

const fetchService = DefaultFetchService.getInstance();
const getPromptsGenerationEndpoint = (campaignId: string): string =>
    `/api/aiTracker/campaigns/${campaignId}/prompts/generate/v2`;

interface IGeneratePromptsParams {
    campaignId: string;
    domain: string;
    cultureName: string;
    topicName: string;
    count: number;
    chatbots: ExplicitLLModel[];
}

const generatePrompts = async ({
    campaignId,
    domain,
    cultureName,
    topicName,
    count,
    chatbots,
}: IGeneratePromptsParams): Promise<ISetupPromptsResponse> => {
    const response = await fetchService.post<{ prompts?: Array<{ topicName: string; query: string; intent: string }> }>(
        getPromptsGenerationEndpoint(campaignId),
        {
            domain,
            cultureName,
            topic: topicName,
            promptCount: count,
            chatbots,
        },
    );

    return groupGeneratedPromptsByTopic(response?.prompts);
};

export const useGeneratePrompts = () =>
    useMutation({
        mutationFn: generatePrompts,
        onError: (error, variables) => {
            Sentry.errorTracker({
                message: "Error generating prompts",
                e: error,
                level: "error",
                params: {
                    extra: {
                        domain: variables.domain,
                        cultureName: variables.cultureName,
                        topic: variables.topicName,
                        count: variables.count,
                    },
                },
            });
        },
    });
